import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listTreasuryWallets,
  syncTreasuryCommunityWallets,
  syncTreasuryFundWallets,
  syncTreasuryWallet,
  updateTreasuryWallet,
} from '../../services/TreasuryService';
import { normalizeTreasuryWalletRows } from './treasuryModels';

export function useTreasuryWallets({
  token,
  communityId,
  clearMessages,
  loadFunds,
  setError,
  setSuccess,
  setSubmitting,
}) {
  const [walletsByFund, setWalletsByFund] = useState({});
  const [walletsLoadingFundId, setWalletsLoadingFundId] = useState('');
  const [walletsErrorByFund, setWalletsErrorByFund] = useState({});
  const [syncSummaries, setSyncSummaries] = useState({ community: null, funds: {}, wallets: {} });
  const alive = useRef(true);
  const reads = useRef(new Map());
  const writing = useRef(false);

  const cancelReads = useCallback(() => {
    for (const controller of reads.current.values()) controller.abort();
    reads.current.clear();
    if (alive.current) setWalletsLoadingFundId('');
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; cancelReads(); };
  }, [cancelReads]);

  const loadFundWallets = useCallback(async (fundId) => {
    if (!fundId || !token || !alive.current) return [];
    reads.current.get(fundId)?.abort();
    const controller = new AbortController();
    reads.current.set(fundId, controller);
    const current = () => alive.current && reads.current.get(fundId) === controller;
    setWalletsLoadingFundId(fundId);
    setWalletsErrorByFund((prev) => ({ ...prev, [fundId]: '' }));
    try {
      const payload = await listTreasuryWallets({ token, fundId, params: { communityId }, signal: controller.signal });
      if (!current()) return [];
      const normalizedRows = normalizeTreasuryWalletRows(payload);
      setWalletsByFund((prev) => ({ ...prev, [fundId]: normalizedRows }));
      return normalizedRows;
    } catch (err) {
      if (current()) {
        setWalletsByFund((prev) => ({ ...prev, [fundId]: [] }));
        setWalletsErrorByFund((prev) => ({ ...prev, [fundId]: err?.message || 'Unable to load treasury wallets.' }));
      }
      return [];
    } finally {
      if (current()) {
        reads.current.delete(fundId);
        setWalletsLoadingFundId((id) => id === fundId ? '' : id);
      }
    }
  }, [communityId, token]);

  const runWalletSync = useCallback(async ({ walletId, fundId = null, scope = 'wallet' }) => {
    if (!alive.current || writing.current) return { success: false, message: 'A wallet operation is already in progress.' };
    writing.current = true;
    cancelReads();
    clearMessages();
    setSubmitting(true);
    try {
      const options = { token, params: { communityId } };
      const response = scope === 'community'
        ? await syncTreasuryCommunityWallets(options)
        : scope === 'fund'
          ? await syncTreasuryFundWallets({ ...options, fundId })
          : await syncTreasuryWallet({ ...options, walletId });
      if (!alive.current) return { success: false };
      cancelReads();
      if (scope === 'community') setWalletsByFund({});
      await loadFunds({ silent: true });
      if (!alive.current) return { success: false };
      if (fundId) await loadFundWallets(fundId);
      if (!alive.current) return { success: false };
      const summary = response.data;
      const failedCount = summary.failedCount ?? (summary.success === false ? 1 : 0);
      setSyncSummaries((prev) => scope === 'community'
        ? { ...prev, community: summary }
        : { ...prev, [scope === 'fund' ? 'funds' : 'wallets']: { ...prev[scope === 'fund' ? 'funds' : 'wallets'], [scope === 'fund' ? fundId : walletId]: summary } });
      if (failedCount > 0) {
        setError(`Sync completed with ${failedCount} wallet failure${failedCount === 1 ? '' : 's'}. Review the sync status before retrying.`);
      } else {
        setSuccess(scope === 'community' ? 'All treasury wallets synced successfully.' : 'Treasury sync completed successfully.');
      }
      return { success: failedCount === 0, data: summary };
    } catch (err) {
      const message = err?.message || 'Treasury sync failed.';
      if (alive.current) {
        if (err?.status === 401 || err?.status === 403) setWalletsByFund({});
        setError(message);
      }
      return { success: false, message };
    } finally {
      writing.current = false;
      if (alive.current) setSubmitting(false);
    }
  }, [cancelReads, clearMessages, communityId, loadFundWallets, loadFunds, setError, setSubmitting, setSuccess, token]);

  const updateWalletDonationConfig = useCallback(async ({ fundId, walletId, donationConfig }) => {
    if (!alive.current || writing.current) return { success: false, message: 'A wallet operation is already in progress.' };
    writing.current = true;
    cancelReads();
    clearMessages();
    setSubmitting(true);
    try {
      await updateTreasuryWallet({ token, params: { communityId }, walletId, updates: { donationConfig } });
      if (!alive.current) return { success: false };
      cancelReads();
      if (fundId) await loadFundWallets(fundId);
      if (!alive.current) return { success: false };
      setSuccess('Wallet donation settings updated successfully.');
      return { success: true };
    } catch (err) {
      const message = err?.message || 'Failed to update wallet donation settings.';
      if (alive.current) {
        if (err?.status === 401 || err?.status === 403) setWalletsByFund({});
        setError(message);
      }
      return { success: false, message };
    } finally {
      writing.current = false;
      if (alive.current) setSubmitting(false);
    }
  }, [cancelReads, clearMessages, communityId, loadFundWallets, setError, setSubmitting, setSuccess, token]);

  return { walletsByFund, walletsLoadingFundId, walletsErrorByFund, syncSummaries, loadFundWallets, runWalletSync, updateWalletDonationConfig };
}
